import { type FC, type ReactNode, useState } from "react";

import { ChevronRight } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import {
  LearnMoreSheet,
  LearnMoreHeader,
  LearnMoreTitle,
  LearnMoreContent,
  LearnMoreFeedback,
  LearnMoreSupportButton,
} from "@/shared/components/ui/LearnMoreSheet";

import { buildPreAuthorityQuestions } from "./commonQuestions";

export interface CommonQuestion {
  label: string;
  answer: ReactNode;
}

interface CommonQuestionsWidgetProps {
  questions?: CommonQuestion[];
}

export const CommonQuestionsWidget: FC<CommonQuestionsWidgetProps> = ({
  questions = buildPreAuthorityQuestions(),
}) => {
  const [selectedQuestion, setSelectedQuestion] = useState<CommonQuestion | null>(null);

  return (
    <>
      <div className="flex flex-col gap-6 border border-primary rounded-lg bg-white p-6">
        <p className="text-base font-semibold tracking-[-0.02em]">Common questions right now</p>
        <div className="flex flex-col gap-3">
          {questions.map((q, i) => (
            <div key={q.label}>
              {i > 0 && <Separator className="mb-3" />}
              <Button
                variant="link"
                onClick={() => setSelectedQuestion(q)}
                className="w-full justify-between gap-3 whitespace-normal text-left font-normal text-secondary tracking-[-0.02em]"
              >
                <span className="flex-1 min-w-0">{q.label}</span>
                <ChevronRight className="size-4 shrink-0" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <LearnMoreSheet
        open={!!selectedQuestion}
        onOpenChange={(open) => {
          if (!open) setSelectedQuestion(null);
        }}
      >
        <LearnMoreHeader>
          <LearnMoreTitle>{selectedQuestion?.label}</LearnMoreTitle>
        </LearnMoreHeader>
        <LearnMoreContent className="px-5 py-5 text-sm text-secondary">
          {selectedQuestion?.answer}
        </LearnMoreContent>
        <LearnMoreFeedback feedbackKey={`common-q-${selectedQuestion?.label}`} />
        <LearnMoreSupportButton />
      </LearnMoreSheet>
    </>
  );
};
